import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon, ChevronUpIcon, CloseLineIcon, MoveIcon } from '../../layout/icons';
import { useDraggableChrome } from '../../layout/useDraggableChrome';
import type { ResultPanelTab } from './sessions';
import '../../styles/rail-drag.css';
import '../../styles/quiz.css';
import '../../styles/submissions.css';

// titleKey is translated at render
const VIEWS: Array<{ id: ResultPanelTab; titleKey: 'results.view.stats' | 'results.view.detail' }> = [
  { id: 'stats', titleKey: 'results.view.stats' },
  { id: 'detail', titleKey: 'results.view.detail' },
];

export interface ResultsCardFrameProps {
  /** What the results are of, e.g. "bài kiểm tra"; the accessible name is the view title plus this. */
  kindLabel: string;
  subtitle: string;
  /** The đề actions, on their own row under the title. */
  actions: ReactNode;
  /** Báo cáo or Chi tiết, picked on the đề's rail. */
  view: ResultPanelTab;
  /** Only when the tab has no rail: a small Báo cáo | Chi tiết switch in the card instead. */
  onView?: (view: ResultPanelTab) => void;
  /** Folded to a bar so the student's work underneath shows. */
  collapsed: boolean;
  /** The student whose work is on the board, named on the folded bar. */
  studentName: string | null;
  /** Footer Thu gọn: fold to the bar. */
  onCollapse: () => void;
  onExpand: () => void;
  /** Header X: close the card; the session and its student's work stay. */
  onClose: () => void;
  children: ReactNode;
}

// The floating results card, one view at a time: title row (view, subtitle, close), action row, body, Thu gọn footer; folded, one bar
export function ResultsCardFrame({ kindLabel, subtitle, actions, view, onView, collapsed, studentName, onCollapse, onExpand, onClose, children }: ResultsCardFrameProps) {
  // one saved spot for every results card, exam or homework, folded or not
  const drag = useDraggableChrome({ id: 'exam-results', enabled: true, refit: collapsed });
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const refocus = useRef(false);
  const { t } = useTranslation('exams');
  const title = t(VIEWS.filter((item) => item.id === view)[0].titleKey);
  const className = ['quiz-results quiz-results--floating', collapsed ? 'quiz-results--collapsed' : '', drag.dragging ? 'quiz-results--dragging' : ''].filter(Boolean).join(' ');

  // the pressed button unmounts, so focus moves to its counterpart rather than dropping to the page
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    if (toggleRef.current) toggleRef.current.focus();
  }, [collapsed]);

  function fold(next: () => void) {
    refocus.current = true;
    next();
  }

  const handle = (
    <button type="button" className="quiz-results__handle" aria-label={t('results.dragHandle')} {...drag.handleProps}>
      <MoveIcon size={18} />
    </button>
  );

  if (collapsed) {
    return (
      <aside ref={drag.ref} className={className} aria-label={`${title} ${kindLabel}`}>
        <div className="quiz-results__header quiz-results__header--bar">
          {handle}
          <div className="quiz-results__heading quiz-results__heading--bar">
            <span className="quiz-results__title">{title}</span>
            {studentName ? <span className="quiz-results__bar-student" title={studentName}>{studentName}</span> : null}
          </div>
          <button ref={toggleRef} type="button" className="quiz-results__close" aria-label={t('results.expandCard')} aria-expanded={false} onClick={() => fold(onExpand)}>
            <ChevronDownIcon size={18} />
          </button>
        </div>
      </aside>
    );
  }

  return (
    <aside ref={drag.ref} className={className} aria-label={`${title} ${kindLabel}`}>
      <header className="quiz-results__header">
        {handle}
        <div className="quiz-results__heading">
          <div className="quiz-results__title">{title}</div>
          <div className="quiz-results__subtitle" title={subtitle}>{subtitle}</div>
        </div>
        <button type="button" className="quiz-results__close" aria-label={t('results.closeCard')} onClick={onClose}>
          <CloseLineIcon size={18} />
        </button>
      </header>
      {onView ? (
        <div className="results-switch" role="group" aria-label={t('results.viewGroup')}>
          {VIEWS.map((item) => (
            <button key={item.id} type="button" aria-pressed={view === item.id} className={view === item.id ? 'results-switch__item is-active' : 'results-switch__item'} onClick={() => onView(item.id)}>{t(item.titleKey)}</button>
          ))}
        </div>
      ) : null}
      <div className="quiz-results__toolbar">{actions}</div>
      <div className="quiz-results__scroll">{children}</div>
      {/* the only fold, bottom-right, within reach on a tall board */}
      <div className="quiz-results__footer">
        <button ref={toggleRef} type="button" className="hw-panel__refresh quiz-results__fold" aria-label={t('results.collapseCard')} aria-expanded={true} onClick={() => fold(onCollapse)}>
          <ChevronUpIcon size={14} /><span>{t('results.collapse')}</span>
        </button>
      </div>
    </aside>
  );
}
